import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { exportReportAsPDF } from '@/utils/reportUtils';
import { supabase } from '@/integrations/supabase/client';
import { format as formatDate } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { OvertimeStatus, TimeRange } from '@/types/enums';
import DonutChart from '@/components/ui/DonutChart';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Department colors
const COLORS = [
  '#4F46E5', '#38BDF8', '#8B5CF6', '#EC4899', '#F97316', '#A3E635',
  '#F59E42', '#F43F5E', '#10B981', '#6366F1', '#FBBF24', '#3B82F6',
];

interface DepartmentData {
  name: string;
  value: number;
  percentage: number;
  avgPerEmployee: number | null;
}

interface DepartmentAllocationReportProps {
  exportMode?: boolean;
  startDate?: Date;
  endDate?: Date;
  department?: string;
}

const fetchDepartmentAllocationData = async (startDate?: Date, endDate?: Date, department?: string) => {
  // First get all departments to ensure we have complete list
  const { data: departments, error: deptError } = await supabase
    .from('departments')
    .select('name')
    .order('name')
  if (deptError || !departments) throw new Error('Failed to fetch departments');

  // Fetch all employees to calculate per-employee averages
  const { data: employees, error: empError } = await supabase
    .from('employees')
    .select('department')
    .not('department', 'is', null)
  if (empError) throw new Error('Failed to fetch employee data');

  // Count employees per department
  const empCountMap: Record<string, number> = {}
  employees?.forEach((emp: any) => {
    if (!emp.department) return
    empCountMap[emp.department] = (empCountMap[emp.department] || 0) + 1
  })

  // Now fetch overtime requests
  let query = supabase
    .from('overtime_requests')
    .select('department, hours')
    .eq('status', OvertimeStatus.APPROVED) // Only show approved requests
  if (department && department !== TimeRange.ALL) {
    query = query.eq('department', department)
  }
  if (startDate) {
    query = query.gte('date', formatDate(startDate, 'yyyy-MM-dd'))
  }
  if (endDate) {
    query = query.lte('date', formatDate(endDate, 'yyyy-MM-dd'))
  }
  const { data: overtimeRequests, error: overtimeError } = await query
  if (overtimeError || !overtimeRequests) throw new Error('Failed to fetch overtime data');

  // Aggregate hours by department
  const deptHours: Record<string, number> = {}
  overtimeRequests.forEach((req: any) => {
    if (!req.department) return
    deptHours[req.department] = (deptHours[req.department] || 0) + Number(req.hours || 0)
  })

  // Calculate total hours for percentage calculation
  const totalHours = Object.values(deptHours).reduce((sum, hours) => sum + hours, 0)

  // Prepare data for chart
  const chartData: DepartmentData[] = departments
    .map((dept: any) => {
      const hours = deptHours[dept.name] || 0
      const empCount = empCountMap[dept.name] || 0
      return {
        name: dept.name,
        value: Number(hours.toFixed(1)),
        percentage: totalHours ? Number(((hours / totalHours) * 100).toFixed(1)) : 0,
        avgPerEmployee: empCount ? Number((hours / empCount).toFixed(1)) : null
      }
    })
    .filter(d => d.value > 0) // Only show departments with overtime
    .sort((a, b) => b.value - a.value) // Sort by hours in descending order

  return chartData;
};

const DepartmentAllocationReport = ({ exportMode = false, startDate, endDate, department }: DepartmentAllocationReportProps) => {
  const { data = [], isLoading, isError } = useQuery<DepartmentData[], Error>({
    queryKey: ['department-allocation', startDate?.toISOString(), endDate?.toISOString(), department],
    queryFn: () => fetchDepartmentAllocationData(startDate, endDate, department),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleUiExport = () => {
    exportReportAsPDF('department-allocation-report-view', 'Department Allocation Report');
    toast.success('Report export initiated successfully');
  };

  const reportInnerContent = () => {
    if (isLoading) return <div className="p-8 text-center">Loading report content...</div>;
    if (isError) return <div className="p-8 text-center text-destructive">Failed to load report data.</div>;
    if (!data.length) return <div className="p-8 text-center text-muted-foreground">No overtime data available for the selected criteria.</div>;

    const totalOvertimeHours = data.reduce((sum, item) => sum + item.value, 0);
    return (
      <>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Department Allocation</h2>
            <p className="text-muted-foreground">Approved overtime distribution by department</p>
          </div>
          {!exportMode && (
            <Button onClick={handleUiExport} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-6 overflow-x-auto min-w-[900px]">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xl font-bold text-left">Visual Distribution</CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => {
                  // Export DonutChart data as CSV
                  const header = ["Department", "Hours"];
                  const csvRows = [header.join(",")];
                  data.forEach((dept) => {
                    csvRows.push([dept.name, dept.value.toFixed(1)].join(","));
                  });
                  const csvContent = csvRows.join("\r\n");
                  const blob = new Blob([csvContent], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "DepartmentOvertimeDistribution.csv";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                disabled={isLoading || !data.length}
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <DonutChart
                  data={data.map((dept, index) => ({
                    name: dept.name,
                    value: dept.value,
                    fill: COLORS[index % COLORS.length],
                  }))}
                  chartConfig={Object.fromEntries(data.map((dept, index) => [
                    dept.name,
                    { label: dept.name, color: COLORS[index % COLORS.length] }
                  ]))}
                  isLoading={isLoading}
                  showLegend={true}
                  showCenterLabel={true}
                  centerLabelValue={data.reduce((sum, d) => sum + d.value, 0).toFixed(1)}
                  centerLabelSubtext="Total"
                  height={300}
                />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold text-left">Department Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.map((dept, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{dept.name}</span>
                      </div>
                      <span className="text-sm">{dept.value} hours ({dept.percentage}%)</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2.5">
                      <div 
                        className="h-2.5 rounded-full" 
                        style={{ 
                          width: `${dept.percentage}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t mt-4">
                  <div className="flex justify-between font-bold">
                    <span>Total Overtime</span>
                    <span>{totalOvertimeHours.toFixed(1)} hours</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-bold text-left">Detailed Breakdown Table</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableCaption>Department overtime breakdown.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Department</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Percentage</TableHead>
                  <TableHead>Avg. Per Employee</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((dept, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        {dept.name}
                      </div>
                    </TableCell>
                    <TableCell>{dept.value.toFixed(1)}h</TableCell>
                    <TableCell>{dept.percentage.toFixed(1)}%</TableCell>
                    <TableCell>{dept.avgPerEmployee !== null ? `${dept.avgPerEmployee.toFixed(1)}h` : 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-bold">Total</TableCell>
                  <TableCell className="font-bold">{totalOvertimeHours.toFixed(1)}h</TableCell>
                  <TableCell className="font-bold">{data.length > 0 ? '100.0%' : '0.0%'}</TableCell>
                  <TableCell className="font-bold">-</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </CardContent>
        </Card>
      </>
    );
  };

  if (exportMode) {
    return (
      <div id="department-allocation-report-gen" className="space-y-6 p-4 bg-white">
        {reportInnerContent()}
      </div>
    );
  } else {
    return (
      <div className="space-y-6" id="department-allocation-report-view">
        {reportInnerContent()}
      </div>
    );
  }
};

export default DepartmentAllocationReport;
